import { useEffect } from "react";

const qaSafe = (value: string | number | undefined | null) =>
  String(value ?? "unknown")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || "unknown";

const setAttrIfMissing = (element: Element, name: string, value: string) => {
  if (!element.getAttribute(name)) element.setAttribute(name, value);
};

const setAttrIfDifferent = (element: Element, name: string, value: string) => {
  if (element.getAttribute(name) !== value) element.setAttribute(name, value);
};

const setQaAttrs = (element: Element, qaId: string) => {
  setAttrIfMissing(element, "id", qaId);
  setAttrIfMissing(element, "data-testid", qaId);
  setAttrIfMissing(element, "data-qa", qaId);
};

const getVisibleText = (element: Element | null | undefined) =>
  (element?.textContent ?? "").replace(/\s+/g, " ").trim();

const getLabelText = (element: Element) => {
  const htmlElement = element as HTMLElement;
  const directLabel = (htmlElement as HTMLInputElement).labels?.[0]?.textContent;
  if (directLabel?.trim()) return directLabel.trim();

  const elementId = element.getAttribute("id");
  if (elementId) {
    const label = document.querySelector(`label[for="${elementId}"]`);
    const labelText = getVisibleText(label);
    if (labelText) return labelText;
  }

  const nearestLabel = element.closest("label");
  const nearestLabelText = getVisibleText(nearestLabel);
  if (nearestLabelText) return nearestLabelText;

  const labelledBy = element.getAttribute("aria-labelledby");
  if (labelledBy) {
    const label = document.getElementById(labelledBy);
    const labelText = getVisibleText(label);
    if (labelText) return labelText;
  }

  const parentText = getVisibleText(element.parentElement);
  if (parentText && parentText.length <= 80) return parentText;

  return "";
};

const readableElementName = (element: Element) => {
  const tag = element.tagName.toLowerCase();
  const aria = element.getAttribute("aria-label");
  const title = element.getAttribute("title");
  const placeholder = element.getAttribute("placeholder");
  const name = element.getAttribute("name");
  const role = element.getAttribute("role");
  const label = getLabelText(element);
  const text = getVisibleText(element);
  const existingId = element.getAttribute("id");
  const type = element.getAttribute("type");

  return (
    aria ||
    title ||
    placeholder ||
    label ||
    (text && text.length <= 80 ? text : "") ||
    name ||
    existingId ||
    role ||
    type ||
    tag
  );
};

const buttonAlias = (rawName: string) => {
  const name = qaSafe(rawName);
  const aliases: Record<string, string> = {
    "add-new-relation": "btn-toolbar-add-new-relation",
    "add-new-group": "btn-toolbar-add-new-group",
    "add-new": "btn-toolbar-add-new",
    "add-relation": "btn-add-relation-submit",
    "update-relation": "btn-update-relation-submit",
    "add-group": "btn-add-group-submit",
    "update-group": "btn-update-group-submit",
    "save": "btn-save",
    "save-relation": "btn-save-relation",
    "save-group": "btn-save-group",
    "cancel": "btn-cancel",
    "close": "btn-close",
    "close-details": "btn-close-view-details",
    "delete": "btn-delete",
    "confirm-delete": "btn-confirm-delete",
    "force-delete": "btn-confirm-soft-force-delete",
    "force-delete-anyway": "btn-confirm-hard-force-delete",
    "restore": "btn-restore",
    "active": "btn-status-active",
    "inactive": "btn-status-inactive",
    "all": "btn-filter-all",
  };
  return aliases[name] ?? `btn-${name}`;
};

const inputAlias = (rawName: string, type?: string | null) => {
  const name = qaSafe(rawName);
  const normalizedType = qaSafe(type ?? "input");
  if (normalizedType === "checkbox") return `checkbox-${name}`;
  if (normalizedType === "radio") return `radio-${name}`;
  if (normalizedType === "date") return `input-${name || "date"}`;
  return `input-${name}`;
};

const selectAlias = (rawName: string) => `select-${qaSafe(rawName)}`;

const dialogAlias = (dialog: Element, fallbackIndex: number) => {
  const title = getVisibleText(dialog.querySelector("h1,h2,h3,h4,h5,h6,[data-radix-dialog-title]"));
  return `dialog-${qaSafe(title || `dialog-${fallbackIndex}`)}`;
};

const getIconName = (element: Element, fallback: string) => {
  const className = String((element as HTMLElement).className || "");
  const fa = className.match(/fa-[a-z0-9-]+/i)?.[0];
  const bi = className.match(/bi-[a-z0-9-]+/i)?.[0];
  const lucide = element.getAttribute("data-lucide");
  return qaSafe(fa || bi || lucide || fallback);
};

export const applyQaAutomationIds = (pagePrefix: string) => {
  const prefix = qaSafe(pagePrefix);
  const root =
    document.querySelector<HTMLElement>(`[data-qa-page="${prefix}"]`) ||
    document.querySelector<HTMLElement>(".h5-container") ||
    document.querySelector<HTMLElement>("main") ||
    document.body;

  if (!root) return;

  setQaAttrs(root, `${prefix}-page`);
  setAttrIfDifferent(root, "data-qa-page", prefix);

  const counters = new Map<string, number>();
  const uniqueId = (suffix: string) => {
    const safeSuffix = qaSafe(suffix);
    const count = counters.get(safeSuffix) ?? 0;
    counters.set(safeSuffix, count + 1);
    return `${prefix}-${safeSuffix}${count === 0 ? "" : `-${count + 1}`}`;
  };

  const mark = (element: Element, suffix: string) => {
    setQaAttrs(element, uniqueId(suffix));
  };

  mark(root, "page");

  root.querySelectorAll("h1,h2,h3,h4,h5,h6").forEach((heading) => {
    mark(heading, `title-${readableElementName(heading)}`);
  });

  root.querySelectorAll("[role='dialog'], dialog").forEach((dialog, index) => {
    mark(dialog, dialogAlias(dialog, index + 1));
  });

  root.querySelectorAll("button,[role='button']").forEach((button) => {
    const suffix = buttonAlias(readableElementName(button));
    mark(button, suffix);
    setAttrIfMissing(button, "data-qa-action", qaSafe(readableElementName(button)));
  });

  root.querySelectorAll("input,textarea,select").forEach((control) => {
    const tag = control.tagName.toLowerCase();
    const type = control.getAttribute("type");
    const name = readableElementName(control);
    const suffix = tag === "select" ? selectAlias(name) : inputAlias(name, type);
    mark(control, suffix);
  });

  root.querySelectorAll("[role='combobox'],[class*='select__control'],[class*='-control']").forEach((selectControl) => {
    mark(selectControl, selectAlias(readableElementName(selectControl)));
  });

  root.querySelectorAll("a[href]").forEach((link) => {
    mark(link, `link-${readableElementName(link)}`);
  });

  root.querySelectorAll("table").forEach((table, tableIndex) => {
    mark(table, tableIndex === 0 ? "table" : `table-${tableIndex + 1}`);
  });

  root.querySelectorAll("thead").forEach((thead, index) => mark(thead, index === 0 ? "table-head" : `table-head-${index + 1}`));
  root.querySelectorAll("tbody").forEach((tbody, index) => mark(tbody, index === 0 ? "table-body" : `table-body-${index + 1}`));
  root.querySelectorAll("tr").forEach((row, index) => mark(row, `table-row-${index + 1}`));
  root.querySelectorAll("th").forEach((cell, index) => mark(cell, `table-header-cell-${index + 1}-${readableElementName(cell)}`));
  root.querySelectorAll("td").forEach((cell, index) => mark(cell, `table-cell-${index + 1}`));

  root.querySelectorAll("svg,i").forEach((icon, index) => {
    const ownerButton = icon.closest("button,[role='button']");
    const buttonName = ownerButton ? readableElementName(ownerButton) : "";
    mark(icon, `icon-${buttonName ? qaSafe(buttonName) : getIconName(icon, `icon-${index + 1}`)}`);
  });

  root.querySelectorAll("[role='alert'],.alert,.toast,[data-state='open']").forEach((message, index) => {
    mark(message, `message-${readableElementName(message) || index + 1}`);
  });
};

export const useQaAutomationIds = (pagePrefix: string) => {
  useEffect(() => {
    const run = () => applyQaAutomationIds(pagePrefix);
    const timer = window.setTimeout(run, 0);
    const root =
      document.querySelector<HTMLElement>(`[data-qa-page="${qaSafe(pagePrefix)}"]`) ||
      document.querySelector<HTMLElement>(".h5-container") ||
      document.body;

    const observer = new MutationObserver(() => {
      window.requestAnimationFrame(run);
    });

    observer.observe(root, { childList: true, subtree: true });

    return () => {
      window.clearTimeout(timer);
      observer.disconnect();
    };
  }, [pagePrefix]);
};

export default useQaAutomationIds;
